import Fuse, { type IFuseOptions } from "fuse.js";
import type { Product } from "../types";
import { SYNONYM_GROUPS, ONE_WAY_SYNONYMS } from "../data/searchSynonyms";

// --- normalization / tokenization ---

function normalize(s: string): string {
  return s.toLowerCase().replace(/ё/g, "е").trim();
}

function tokenize(s: string): string[] {
  return normalize(s)
    .split(/[^a-zа-я0-9]+/iu)
    .filter((t) => t.length > 0);
}

const STOPWORDS = new Set([
  "для", "и", "с", "со", "от", "на", "в", "во", "по", "из", "к", "о", "у", "а", "но",
  "the", "for", "and", "with", "of", "a", "an", "or",
]);

/**
 * Lightweight suffix-stripping stemmer — not a real morphological analyzer
 * (no dictionary, no POS awareness), so it only bridges case/number
 * inflection within the same word ("ножи"/"ножа"/"ножей" -> "нож"). It
 * cannot bridge a noun to a derived adjective ("мясо" -> "мясной" isn't a
 * suffix swap, it's a different word) — those go in searchSynonyms.ts's
 * SYNONYM_GROUPS instead, alongside every other alternate-phrasing pair.
 */
function stemRu(word: string): string {
  const endings = [
    "иями", "ями", "ами", "иях", "ях", "ах",
    "иев", "ев", "ов", "ей",
    "ию", "ем", "ом", "им", "ым", "ых", "их",
    "ое", "ая", "ые", "ий", "ый", "юю", "ую",
    "и", "ы", "а", "я", "у", "ю", "е", "о", "й", "ь",
  ];
  for (const e of endings) {
    if (word.length - e.length >= 3 && word.endsWith(e)) return word.slice(0, -e.length);
  }
  return word;
}

function stemEn(word: string): string {
  if (word.length > 4 && word.endsWith("ies")) return word.slice(0, -3) + "y";
  if (word.length > 4 && word.endsWith("es")) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith("s") && !word.endsWith("ss")) return word.slice(0, -1);
  return word;
}

function stem(word: string): string {
  return /^[a-z]/i.test(word) ? stemEn(word) : stemRu(word);
}

/** Tokenizes, drops stopwords, and stems what's left — the unit every tier matches on. */
function contentStems(text: string): string[] {
  const tokens = tokenize(text).filter((t) => !STOPWORDS.has(t));
  const kept = tokens.length > 0 ? tokens : tokenize(text);
  return kept.map(stem);
}

// --- guarded fuzzy matching ---

/**
 * Bounded Levenshtein distance — returns false as soon as it's certain the
 * true distance exceeds max, via a length-difference pre-check and a
 * per-row minimum early exit, rather than compute the exact number.
 */
function levenshteinWithin(a: string, b: string, max: number): boolean {
  if (max === 0) return a === b;
  if (Math.abs(a.length - b.length) > max) return false;
  if (a === b) return true;
  let prevRow = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const currRow = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const value = Math.min(prevRow[j] + 1, currRow[j - 1] + 1, prevRow[j - 1] + cost);
      currRow.push(value);
      if (value < rowMin) rowMin = value;
    }
    if (rowMin > max) return false;
    prevRow = currRow;
  }
  return prevRow[b.length] <= max;
}

/**
 * Edit-distance budget by stem length. Distance 2 on a short word is far
 * looser than it sounds (on a 5-letter word it lets nearly half the
 * characters differ) — that's what let "steak" fuzzy-match "tea" and
 * "knife" match "life" independently in earlier testing, which together
 * false-positived an unrelated product. Kept intentionally tight:
 * 3 chars or fewer: exact only. 4-8: one edit. 9+: two edits.
 */
function maxTypoDistance(len: number): number {
  if (len <= 3) return 0;
  if (len <= 8) return 1;
  return 2;
}

/**
 * Fuzzy neighbors of one stem within the TITLE-zone vocabulary only — never
 * called against description/spec text. Returns just the stem itself
 * (skipping the search) when an exact match already exists in that
 * vocabulary, since guessing at typos is only useful when the literal term
 * isn't there at all.
 */
function fuzzyStemMatches(stem: string, titleVocabulary: Set<string>): string[] {
  if (titleVocabulary.has(stem)) return [stem];
  const maxDist = maxTypoDistance(stem.length);
  if (maxDist === 0) return [stem];
  const matches = [stem];
  for (const candidate of titleVocabulary) {
    if (Math.abs(candidate.length - stem.length) > maxDist) continue;
    if (levenshteinWithin(stem, candidate, maxDist)) matches.push(candidate);
  }
  return matches;
}

// --- synonym dictionary, flattened once at module load ---

const SYNONYM_PAIRS: Array<[string, string]> = (() => {
  const pairs: Array<[string, string]> = [];
  for (const group of SYNONYM_GROUPS) {
    const normalized = group.map(normalize);
    for (let i = 0; i < normalized.length; i++) {
      for (let j = 0; j < normalized.length; j++) {
        if (i !== j) pairs.push([normalized[i], normalized[j]]);
      }
    }
  }
  for (const [from, tos] of Object.entries(ONE_WAY_SYNONYMS)) {
    for (const to of tos) pairs.push([normalize(from), normalize(to)]);
  }
  return pairs;
})();

/** Phrase-level substring replacement — multi-word synonyms work the same as single-word ones. */
function synonymVariantsOf(normalizedQuery: string): string[] {
  const variants = new Set<string>();
  for (const [from, to] of SYNONYM_PAIRS) {
    if (normalizedQuery.includes(from)) {
      const variant = normalizedQuery.split(from).join(to);
      if (variant !== normalizedQuery) variants.add(variant);
    }
  }
  return [...variants];
}

// --- per-product index data ---

interface SearchableProduct {
  product: Product;
  nameNorm: string;
  modelNorm: string;
  idNorm: string;
  /** Stems from name/model/keywords/id — Title/SKU/Tags. Fuzzy-eligible. */
  titleStems: Set<string>;
  /** Stems from specs/characteristics/brand/category. Exact-match only, never fuzzy. */
  broadStems: Set<string>;
  fuseRecord: {
    name: string;
    model: string;
    brand: string;
    category: string;
    specs: string;
    characteristics: string;
    keywords: string;
  };
}

function buildSearchable(product: Product): SearchableProduct {
  const titleText = `${product.name} ${product.model} ${(product.searchKeywords ?? []).join(" ")} ${product.id}`;
  const broadText = `${product.specs.join(" ")} ${
    product.characteristics ? Object.values(product.characteristics).join(" ") : ""
  } ${product.brand ?? ""} ${product.category} ${product.subcategory ?? ""}`;
  return {
    product,
    nameNorm: normalize(product.name),
    modelNorm: normalize(product.model),
    idNorm: normalize(product.id),
    titleStems: new Set(tokenize(titleText).map(stem)),
    broadStems: new Set(tokenize(broadText).map(stem)),
    fuseRecord: {
      name: product.name,
      model: product.model,
      brand: product.brand ?? "",
      category: `${product.category} ${product.subcategory ?? ""}`,
      specs: product.specs.join(" "),
      characteristics: product.characteristics ? Object.values(product.characteristics).join(" ") : "",
      keywords: (product.searchKeywords ?? []).join(" "),
    },
  };
}

const FUSE_OPTIONS: IFuseOptions<SearchableProduct["fuseRecord"]> = {
  keys: [
    { name: "name", weight: 3 },
    { name: "keywords", weight: 2.5 },
    { name: "model", weight: 2.5 },
    { name: "brand", weight: 1.5 },
    { name: "category", weight: 1 },
    { name: "specs", weight: 1 },
    { name: "characteristics", weight: 0.5 },
  ],
  threshold: 0.3,
  ignoreLocation: true,
  minMatchCharLength: 2,
};

export interface ProductSearchIndex {
  items: SearchableProduct[];
  titleVocabulary: Set<string>;
  fuse: Fuse<SearchableProduct["fuseRecord"]>;
  byRecord: Map<SearchableProduct["fuseRecord"], Product>;
}

/**
 * Builds the search index once per catalog load — this is the expensive
 * part (indexing 1600+ products), so callers should memoize this on the
 * `products` array and reuse it across every keystroke.
 */
export function createProductSearchIndex(products: Product[]): ProductSearchIndex {
  const items = products.map(buildSearchable);
  const titleVocabulary = new Set<string>();
  for (const item of items) for (const s of item.titleStems) titleVocabulary.add(s);
  const byRecord = new Map(items.map((item) => [item.fuseRecord, item.product]));
  return {
    items,
    titleVocabulary,
    fuse: new Fuse(items.map((i) => i.fuseRecord), FUSE_OPTIONS),
    byRecord,
  };
}

/** For each query stem, its accepted variant set (itself, plus any guarded fuzzy neighbor). */
function buildStemVariantSets(stems: string[], titleVocabulary: Set<string>): string[][] {
  return stems.map((s) => fuzzyStemMatches(s, titleVocabulary));
}

/**
 * Strict AND: every query stem must land somewhere (title zone allows the
 * guarded-fuzzy variant set; the description/spec zone only ever accepts
 * the literal stem, never a fuzzy one). Returns null on any miss.
 */
function scoreIfAllStemsMatch(item: SearchableProduct, stemVariantSets: string[][]): number | null {
  let score = 0;
  for (const variants of stemVariantSets) {
    if (variants.some((v) => item.titleStems.has(v))) {
      score += 3;
      continue;
    }
    if (item.broadStems.has(variants[0])) {
      score += 1;
      continue;
    }
    return null;
  }
  return score;
}

interface ScoredMatch {
  /** From the strict AND stem match alone — every Phase 1 survivor already
   * satisfies the AND-gate, so this only varies by title- vs broad-zone
   * weighting. The cutoff applies to THIS, never to sortScore below. */
  stemScore: number;
  /** stemScore plus the exact-phrase bonus — used only for ordering. */
  sortScore: number;
}

/**
 * Score relative to the top hit in this result set — drops anything under
 * 40% of it. Deliberately cuts on stemScore, not the bonus-inflated
 * sortScore: every candidate here already passed the strict AND-gate
 * (every stem matched, see scoreIfAllStemsMatch), so a +100 exact-phrase
 * bonus on a couple of results must never be able to drag the cutoff high
 * enough to exclude other equally-valid AND matches that just don't
 * happen to contain the literal phrase — that inflated the cutoff to
 * exclude 96% of otherwise-correct meat-knife results in testing.
 */
function applyCutoffAndRank(scored: Map<string, ScoredMatch>, byId: Map<string, Product>): Product[] {
  const entries = [...scored.entries()];
  if (entries.length === 0) return [];
  const topStemScore = Math.max(...entries.map(([, s]) => s.stemScore));
  const cutoff = topStemScore * 0.4;
  return entries
    .filter(([, s]) => s.stemScore >= cutoff)
    .sort((a, b) => b[1].sortScore - a[1].sortScore)
    .map(([id]) => byId.get(id))
    .filter((p): p is Product => p !== undefined);
}

/**
 * Phase 1 — strict AND across every content stem of the query, tried
 * against the raw query and every synonym-expanded rewrite of it. A
 * product qualifies only if ALL of a variant's stems match (guarded fuzzy
 * in the title zone, exact-only in the description zone); the best score
 * across variants wins. Returns [] if nothing clears the bar — callers
 * must not mix in anything looser when this returns results.
 */
function phase1(index: ProductSearchIndex, rawQuery: string): Product[] {
  const normalizedQuery = normalize(rawQuery);
  const exactPhraseVariants = [normalizedQuery, ...synonymVariantsOf(normalizedQuery)];
  const byId = new Map(index.items.map((item) => [item.product.id, item.product]));
  const scored = new Map<string, ScoredMatch>();

  for (const variantText of exactPhraseVariants) {
    const stems = contentStems(variantText);
    if (stems.length === 0) continue;
    const stemVariantSets = buildStemVariantSets(stems, index.titleVocabulary);
    for (const item of index.items) {
      const stemScore = scoreIfAllStemsMatch(item, stemVariantSets);
      if (stemScore === null) continue;
      const exactPhraseBonus =
        item.nameNorm.includes(variantText) ||
        item.modelNorm.includes(variantText) ||
        item.idNorm.includes(variantText)
          ? 100
          : 0;
      const sortScore = stemScore + exactPhraseBonus;
      const prev = scored.get(item.product.id);
      if (prev === undefined || sortScore > prev.sortScore) {
        scored.set(item.product.id, { stemScore, sortScore });
      }
    }
  }

  return applyCutoffAndRank(scored, byId);
}

/**
 * Phase 2 — only reached when Phase 1 found literally nothing. Matches on
 * the single most specific content stem (the one with the fewest exact
 * title-zone hits among those with at least one — a proxy for "the head
 * noun" without real POS tagging: a generic modifier like a material or
 * finish tends to hit far more products than the actual item being asked
 * for). Falls through to the broad Fuse.js pass only if no single stem
 * matches anything either.
 */
function phase2(index: ProductSearchIndex, rawQuery: string): Product[] {
  const stems = contentStems(rawQuery);
  let best: { ids: Set<string> } | null = null;
  for (const s of stems) {
    const ids = new Set<string>();
    for (const item of index.items) {
      if (item.titleStems.has(s)) ids.add(item.product.id);
    }
    if (ids.size === 0) continue;
    if (!best || ids.size < best.ids.size) best = { ids };
  }
  if (best) {
    const byId = new Map(index.items.map((item) => [item.product.id, item.product]));
    return [...best.ids].map((id) => byId.get(id)).filter((p): p is Product => p !== undefined);
  }

  return index.fuse.search(rawQuery).map((result) => index.byRecord.get(result.item)!).filter(Boolean);
}

/**
 * Instant client-side search against a pre-built index. Runs entirely in
 * the browser, so results are ranked and returned synchronously with no
 * network round trip. Two-phase, not tiered-and-blended: Phase 1 (strict
 * AND across every query token/stem, synonym-aware, guarded fuzzy) either
 * returns a clean result set on its own or nothing at all — never diluted
 * by looser matches. Phase 2's broad fallback only ever runs when Phase 1
 * came back completely empty.
 */
export function searchProducts(index: ProductSearchIndex, query: string): Product[] {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const precise = phase1(index, trimmed);
  if (precise.length > 0) return precise;

  return phase2(index, trimmed);
}
